import { TokenLogo } from "../components/TokenSelect";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Sparkles, Database, BadgeInfo, BadgeCheck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api, errMsg } from "../lib/api";
import { fmtUsd, fmtPct, short } from "../lib/format";
import { OriginalBadge } from "../components/OriginalBadge";
import { Loading, ErrorState, Empty } from "../components/State";

const PageHead = ({ kicker, title, children }) => (
  <div className="mb-8">
    <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-lime mb-2">{kicker}</p>
    <h1 className="hc-title font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight">{title}</h1>
    {children && <div className="mt-3 text-sm sm:text-base text-slate-400 max-w-3xl">{children}</div>}
  </div>
);
export { PageHead };

const ListedGrid = ({ q }) => {
  const [s, setS] = useState({ loading: true, items: [], error: null });
  useEffect(() => {
    const t = setTimeout(() => api.get("/listings", { params: q ? { q } : {} }).then((r) => setS({ loading: false, items: r.data, error: null }))
      .catch((e) => setS({ loading: false, items: [], error: errMsg(e) })), 300);
    return () => clearTimeout(t);
  }, [q]);
  if (s.loading) return <Loading />;
  if (s.error) return <ErrorState message={s.error} />;
  if (!s.items.length) return <Empty testid="explore-listed-empty" title="No approved HIVEVORA listings yet" hint="Projects can apply on the Token Listings page. Every listing is reviewed; approval is not an audit or endorsement." />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {s.items.map((l) => (
        <Link key={l.id} to={`/token/${l.contract_address}`} data-testid={`listed-token-${l.onchain.symbol}`} className="glass rounded-xl p-5 hover:border-lime/40 transition-colors">
          <div className="flex items-center gap-3">
            <TokenLogo t={{ symbol: l.metadata.symbol, address: l.contract_address, logo: l.metadata.logo_url }} size={40} />
            <div className="min-w-0">
              <p className="font-display font-bold text-white truncate">{l.onchain.symbol} <span className="text-slate-500 text-xs font-sans">{l.onchain.name}</span></p>
              <p className="text-[11px] font-mono text-slate-500">{short(l.contract_address, 6)}</p>
            </div>
            {l.featured && <span className="ml-auto rounded bg-warning/15 px-1.5 py-0.5 text-[9px] font-bold text-warning">SPONSORED</span>}
          </div>
          <p className="text-xs text-slate-400 mt-3 line-clamp-2">{l.metadata.description}</p>
        </Link>
      ))}
    </div>
  );
};

const IndexedTable = ({ q }) => {
  const [s, setS] = useState({ loading: true, pools: [], error: null });
  useEffect(() => {
    const t = setTimeout(() => {
      setS((x) => ({ ...x, loading: true }));
      (q ? api.get("/market/search", { params: { q } }) : api.get("/market/top-pools")).then((r) => setS({ loading: false, pools: r.data.pools, error: null }))
        .catch((e) => setS({ loading: false, pools: [], error: errMsg(e) }));
    }, 400);
    return () => clearTimeout(t);
  }, [q]);
  if (s.loading) return <Loading />;
  if (s.error) return <ErrorState message={s.error} />;
  if (!s.pools.length) return <Empty title="No pools match" />;
  return (
    <div className="glass rounded-xl overflow-x-auto">
      <table className="w-full text-sm" data-testid="explore-indexed-table">
        <thead><tr className="text-[10px] uppercase tracking-widest text-slate-500 font-mono border-b border-white/5">
          {["Pair", "DEX", "Price", "24h", "Volume 24h", "Liquidity", "FDV"].map((h) => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}
        </tr></thead>
        <tbody>
          {s.pools.map((p) => (
            <tr key={p.address} className="border-b border-white/5 hover:bg-lime/[0.03]">
              <td className="px-4 py-3"><Link data-testid={`indexed-pool-${p.address}`} to={`/token/${p.base_token.address}`} className="flex items-center gap-2 font-semibold text-white hover:text-lime">
                <TokenLogo t={p.base_token} size={24} />{p.name}</Link></td>
              <td className="px-4 py-3 text-xs font-mono text-slate-400">{p.dex}</td>
              <td className="px-4 py-3 font-mono">{fmtUsd(p.price_usd, false)}</td>
              <td className={`px-4 py-3 font-mono ${p.change_24h_pct >= 0 ? "text-lime" : "text-danger"}`}>{fmtPct(p.change_24h_pct)}</td>
              <td className="px-4 py-3 font-mono">{fmtUsd(p.volume_24h_usd)}</td>
              <td className="px-4 py-3 font-mono">{fmtUsd(p.liquidity_usd)}</td>
              <td className="px-4 py-3 font-mono text-slate-400">{fmtUsd(p.fdv_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const KINDS = [["all", "All tokens"], ["crypto", "Crypto"], ["robinhood_stock_token", "Stock Tokens"], ["hive_launch", "HIVEVORA launches"]];

const AllTokens = ({ q }) => {
  const [kind, setKind] = useState("all");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [more, setMore] = useState(false);
  const load = (skip = 0) => api.get("/tokens/all", { params: { q, kind, limit: 60, skip } });
  useEffect(() => {
    setData(null); setErr(null);
    const t = setTimeout(() => load().then((r) => setData(r.data)).catch((e) => setErr(errMsg(e))), 250);
    return () => clearTimeout(t);
  }, [q, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadMore = async () => {
    setMore(true);
    try { const r = await load(data.tokens.length); setData({ ...r.data, tokens: [...data.tokens, ...r.data.tokens] }); } catch (e) { setErr(errMsg(e)); } finally { setMore(false); }
  };
  return (
    <div data-testid="all-tokens-panel">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {KINDS.map(([k, l]) => <button key={k} data-testid={`tokens-kind-${k}`} onClick={() => setKind(k)} className={`rounded-full border px-3 py-1 text-xs ${kind === k ? "border-lime bg-lime/15 text-lime" : "border-white/10 text-slate-400 hover:text-white"}`}>{l}</button>)}
        {data && <span data-testid="tokens-total" className="text-xs text-slate-500 ml-auto">{data.total} tokens · {data.index.running ? "indexing all DEXes…" : `indexed ${data.index.pools || "—"} pools across ${data.index.dexes || "—"} DEXes`}</span>}
      </div>
      {err ? <ErrorState message={err} testid="all-tokens-error" /> : !data ? <Loading /> : !data.tokens.length ? <Empty title="No tokens found" /> : (
        <>
          <div className="glass rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="px-4 py-3">Token</th><th className="px-4 py-3 text-right">Price</th><th className="px-4 py-3 text-right">Liquidity</th><th className="px-4 py-3 text-right">Vol 24h</th><th className="px-4 py-3 text-right">Pools</th><th className="px-4 py-3">Type</th>
              </tr></thead>
              <tbody>
                {data.tokens.map((t) => (
                  <tr key={t.address} className="border-b border-white/5 hover:bg-lime/[0.03]">
                    <td className="px-4 py-2.5"><Link data-testid={`token-row-${t.address}`} to={`/token/${t.address}`} className="flex items-center gap-3 hover:text-lime">
                      <TokenLogo t={t} size={30} /><span className="min-w-0"><span className="flex items-center gap-1.5 font-semibold text-white truncate max-w-[220px]">{t.symbol} <OriginalBadge address={t.address} /></span><span className="block text-[11px] text-slate-500 truncate max-w-[220px]">{t.name.replace(" • Robinhood Token", "")}</span></span></Link></td>
                    <td className="px-4 py-2.5 text-right font-mono">{t.price_usd ? fmtUsd(t.price_usd) : "—"}</td>
                    <td className={`px-4 py-2.5 text-right font-mono ${t.liquidity_usd && t.liquidity_usd < 1000 ? "text-warning" : ""}`}>{t.liquidity_usd ? fmtUsd(t.liquidity_usd) : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{t.volume_24h_usd ? fmtUsd(t.volume_24h_usd) : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-400">{t.pools || "—"}</td>
                    <td className="px-4 py-2.5 text-[10px] font-bold">{t.kind === "robinhood_stock_token" ? <span className="text-lime">STOCK TOKEN</span> : t.kind === "hive_launch" ? <span className="text-lime">HIVEVORA LAUNCH</span> : t.verified ? <span className="text-lime">CORE</span> : <span className="text-slate-400">INDEXED</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.tokens.length < data.total && <button data-testid="tokens-load-more" onClick={loadMore} disabled={more} className="mt-4 rounded-lg border border-lime/40 px-4 py-2 text-xs text-lime hover:bg-lime/10">{more ? "Loading…" : `Load more (${data.total - data.tokens.length} left)`}</button>}
          <p className="mt-3 text-[11px] text-slate-500">Liquidity = summed USD reserves of indexed pools containing the token (GeckoTerminal). INDEXED tokens are unreviewed — being listed here is not an endorsement.</p>
        </>
      )}
    </div>
  );
};

export default function Explore() {
  const [q, setQ] = useState("");
  return (
    <div className="mx-auto max-w-[1500px] px-4 lg:px-8 py-10" data-testid="explore-page">
      <PageHead kicker="Token explorer" title="Explore Robinhood Chain">
        Two clearly separated sources: <b className="text-slate-200">HIVEVORA-reviewed listings</b> (user-submitted metadata, on-chain facts verified by our backend) and
        <b className="text-slate-200"> indexed pools</b> (raw market data from GeckoTerminal, not reviewed by HIVEVORA).
      </PageHead>
      <div className="relative max-w-xl mb-6">
        <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
        <input data-testid="explore-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search symbol, name or address" className="field pl-9" />
      </div>
      <Tabs defaultValue="robinhood">
        <TabsList className="bg-black/40 border border-white/10 flex-wrap h-auto justify-start">
          <TabsTrigger value="listed" data-testid="explore-tab-listed" className="data-[state=active]:bg-lime data-[state=active]:text-black gap-1.5"><Sparkles className="h-3.5 w-3.5" /> HIVEVORA listings</TabsTrigger>
          <TabsTrigger value="robinhood" data-testid="explore-tab-robinhood" className="data-[state=active]:bg-lime data-[state=active]:text-black gap-1.5"><BadgeCheck className="h-3.5 w-3.5" /> All tokens</TabsTrigger>
          <TabsTrigger value="indexed" data-testid="explore-tab-indexed" className="data-[state=active]:bg-lime data-[state=active]:text-black gap-1.5"><Database className="h-3.5 w-3.5" /> Indexed pools</TabsTrigger>
        </TabsList>
        <TabsContent value="listed" className="mt-6"><ListedGrid q={q} /></TabsContent>
        <TabsContent value="robinhood" className="mt-6"><AllTokens q={q} /></TabsContent>
        <TabsContent value="indexed" className="mt-6"><IndexedTable q={q} /></TabsContent>
      </Tabs>
      <p className="mt-8 flex gap-2 text-xs text-slate-500"><BadgeInfo className="h-4 w-4 shrink-0" /> Liquidity = USD value of pool reserves. FDV = price × total supply. Market cap = price × circulating supply when the provider reports it. None of these are guarantees of safety or value.</p>
    </div>
  );
}
